import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import { default as ReactSelect } from 'react-select';
import AsyncSelect from 'react-select/async';
import { COLORS } from '../../theme';
import CreatableSelect from 'react-select/creatable';
import isEmpty from 'lodash/isEmpty';

export default function Select(props) {
	const [inputValue, setInputValue] = useState("");
	const { onChange, source, formatOption, value, isInvalid, isValid, options, style, creatable, loadOptions, ...filteredProps } = props

  const optionsFromProps = (() => {
	  if (options) {
	    return options.map (option => (typeof(option) === 'string' ? {value: option, label: option} : option))
    } else {
	    return [];
    }
  })();

	const optionsFromChildren = props.children ? React.Children.map(props.children, (child) => {
    if (child) {
      return {
        value: child.props.value,
        label: child.props.children
      }
    }
  }) : [];

	const getOptions = () => (options ? optionsFromProps : optionsFromChildren)

  const [selectOptions, setSelectOptions] = useState(getOptions())

  useEffect(() => {
    setSelectOptions(getOptions())
  }, [options, props.children])

  const customStyles = {
	  container: (provided, state) => ({
	    ...style
    }),
    control: (provided, state) => {
      const focusedStyle = state.isFocused ? {
        color: COLORS.darkGrey,
        borderColor: COLORS.primary,
        outline: 0,
        boxShadow: `0 0 0 0.2rem ${COLORS.primary}50`,
        zIndex: 10000
      } : {}
      const isInvalidStyle = isInvalid ? {borderColor: "#dc3545"} : {};
      const isValidStyle = isValid ? {borderColor: "#28a745"} : {};
      return ({
        borderColor: state.isDisabled ? "#ced4da" : "initial",
        ...provided,
        ...focusedStyle,
        ...isInvalidStyle,
        ...isValidStyle,
        "&:hover": {
          borderColor: null
        },
        // backgroundColor: state.isDisabled ? "#e9ecef" : "inherit"
        backgroundColor: state.isDisabled ? "#e9ecef" : "white"
      })
    },
    singleValue: (provided, state) => ({
      color: state.isDisabled ? "#495057" : "inherit"
    }),
    menuPortal: (provided, state) => ({
      ...provided,
      zIndex: 10000
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? COLORS.primary : 'inherit',
      color: state.isSelected ? 'white' : (state.isDisabled ? '#AAAAAA' : 'inherit'),
      "&:hover": {
        backgroundColor: `${COLORS.primary}50`
      }
    }),
    valueContainer: (provided, state) => {
      const isInvalidStyle = isInvalid ? {
        paddingRight: "calc(1.5em + 0.75rem)",
        backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='%23dc3545' viewBox='-2 -2 7 7'%3e%3cpath stroke='%23dc3545' d='M0 0l3 3m0-3L0 3'/%3e%3ccircle r='.5'/%3e%3ccircle cx='3' r='.5'/%3e%3ccircle cy='3' r='.5'/%3e%3ccircle cx='3' cy='3' r='.5'/%3e%3c/svg%3E\")",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center right calc(0.375em + 0.1875rem)",
        backgroundSize: "calc(0.75em + 0.375rem) calc(0.75em + 0.375rem)"
      } : {};
      const isValidStyle = isValid ? {
        paddingRight: "calc(1.5em + 0.75rem)",
        backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 8'%3e%3cpath fill='%2328a745' d='M2.3 6.73L.6 4.53c-.4-1.04.46-1.4 1.1-.8l1.1 1.4 3.4-3.8c.6-.63 1.6-.27 1.2.7l-4 4.6c-.43.5-.8.4-1.1.1z'/%3e%3c/svg%3e\")",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center right calc(0.375em + 0.1875rem)",
        backgroundSize: "calc(0.75em + 0.375rem) calc(0.75em + 0.375rem)"
      } : {};
      return {
        ...provided,
        ...isValidStyle,
        ...isInvalidStyle
      }
    }
  }

  const getSelectProps = (filteredProps) => ({
	...filteredProps,
    // className: isValid ? "is-valid" : (isInvalid ? "is-invalid" : ""),
		menuPortalTarget: document.querySelector('body'),
		onChange: (opt, action) => {
			let val;
			if(Array.isArray(opt)) {
				val = opt.map(x => x.value)
			} else {
				val = opt ? opt.value : opt
			}
      onChange(val)
		},
		styles: customStyles
	});

  const selectProps = getSelectProps(filteredProps);

	function handleInputChange(newValue) {
    // const inputValue = newValue.replace(/\W/g, '');
    // setInputValue(inputValue);
    // return inputValue
    setInputValue(newValue)
  }

  function flattenOptions(opts) {
	  if (opts[0] && opts[0].hasOwnProperty("options")) {
	    return opts.reduce((acc, val) => {
	      return [...acc, ...val.options]
      }, [])
    } else {
	    return opts
    }
  }

  if (loadOptions) {
    return (<React.Fragment>
      <AsyncSelect
        {...selectProps}
        cacheOptions
        loadOptions={loadOptions}
        defaultOptions={selectOptions}
        onInputChange={handleInputChange}
        value={value}
      />
    </React.Fragment>)
  } else {
	  const flattenedOptions = flattenOptions(selectOptions)
    const selected = Array.isArray(value) ? flattenedOptions.filter( x => (value.includes(x.value))) : flattenedOptions.filter( x => (x.value === value && typeof(x.value) === typeof(value)))
	  if (creatable) {
	    const addOption = (newVal) => {
        setSelectOptions(prevState => [...prevState, {value: newVal, label: newVal}])
      }
      if (selected.length === 0 && !isEmpty(value)) {
        if ( props.isMulti ) {
          value.forEach(element => { addOption(element) });
        } else {
          addOption(value)
        }
      }
      const handleCreateOption = (newVal) => {
        addOption(newVal)
        if ( props.isMulti ) {
          onChange([newVal, ...value])
        } else {
          onChange(newVal)
        }
      }
      return (
        <CreatableSelect
          createOptionPosition={"first"}
          {...selectProps}
          onCreateOption={handleCreateOption}
          options={selectOptions}
          value={ selected || null}
        />)
    } else {
      return (<ReactSelect
        {...selectProps}
        options={selectOptions}
        value={ selected || null}
      />)
    }
  }
}

Select.propTypes = {
	onChange: PropTypes.func,
	name: PropTypes.string,
	children: PropTypes.oneOfType([
		PropTypes.arrayOf(
			PropTypes.shape({
				value: PropTypes.string,
				children: PropTypes.node
			})),
		PropTypes.shape({
			value: PropTypes.string,
			children: PropTypes.node
		})
	]),
  isValid: PropTypes.bool,
  isInvalid: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.oneOfType([PropTypes.element, PropTypes.string]).isRequired
    }),
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.element, PropTypes.string]).isRequired,
      options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.oneOfType([PropTypes.element, PropTypes.string]).isRequired
      })).isRequired
    })
  ]))
}
